import React, { Fragment, useEffect } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { Trans } from '@lingui/macro';
import { linkTo } from 'utils/links';

// Redux
import { getMyPlaylists } from 'store/modules/users/actions';
import { loadSong } from 'store/modules/music/actions';
import { getLoadedSong, isSongError } from 'store/modules/music/selectors';
import { getMyPlaylists as getPlaylists } from 'store/modules/users/selectors';
import { getCurrentUser } from 'store/modules/auth/selectors';

import Detail from 'containers/Song/Detail';
import HeaderTitle from 'containers/HeaderTitle';
import NotFound from 'containers/Layout/NotFound';
import CommentForm from 'containers/Comments/CommentForm';
import CommentList from 'containers/Comments/CommentList';

const mapState = state => ({
  current: getCurrentUser(state),
  errata: isSongError(state),
  song: getLoadedSong(state),
  playlists: getPlaylists(state),
});

export default function Song({ songId }) {
  const { current, errata, song, playlists } = useMappedState(mapState);
  const dispatch = useDispatch();
  const isSingle = song.album && song.album.type === 'single';
  useEffect(() => {
    const shouldLoad = isNaN(songId) ? song.slug !== songId : song.id !== parseInt(songId, 10);
    if (shouldLoad) {
      dispatch(loadSong(songId));
      if (current.id) {
        dispatch(getMyPlaylists());
      }
    }
  }, [songId, current]);

  if (errata) {
    return <NotFound />
  }

  return (
    <div className="h-full overflow-auto scrolling-touch flex flex-col px-4 md:pt-12 md:px-8 md:flex-row">
      <HeaderTitle title={song.title} />
      <div className="mb-8 md:w-1/3 md:pr-4 xl:pr-6">
        <Detail
          model={song}
          list={[song]}
          image={song.album.image}
          title={song.title}
          link1={!isSingle && linkTo('/albums/:slug', song.album)}
          link1Label={!isSingle && song.album.title}
          link2={linkTo('/members/profiles/:slug', song.author)}
          link2Label={song.author.name}
          released={song.album.released}
          playlists={playlists}
          isLoggedIn={!!current.id}
        />
      </div>
      <div className="md:w-2/3 md:pl-4 xl:pl-6">
        { song.description &&
          <Fragment>
            <h3 className="mb-1 text-xl font-sans text-secondary"><Trans>Description</Trans></h3>
            <p className="mb-8 text-base leading-normal whitespace-pre-wrap text-secondary">{song.description}</p>
          </Fragment>
        }
        { song.lyric &&
          <Fragment>
            <h3 className="mb-1 text-xl font-sans text-secondary"><Trans>Lyrics</Trans></h3>
            <p className="mb-8 text-base leading-normal whitespace-pre-wrap text-secondary">{song.lyric}</p>
          </Fragment>
        }
        <div className="md:flex">
          {song.plays > 0 && <Property value={`${song.plays}`}><Trans>Plays</Trans></Property>}
          {song.favorites > 0 && <Property value={`${song.favorites}`}><Trans>Likes</Trans></Property>}
          {song.bpm && <Property value={song.bpm}><Trans>BPM</Trans></Property>}
          {song.moods && <Property value={song.moods}><Trans>Moods</Trans></Property>}
          {song.instruments && <Property value={song.instruments}><Trans>Instruments</Trans></Property>}
          {song.vocals && <Property value={song.vocals}><Trans>Vocals</Trans></Property>}
          {song.tags && song.tags.length > 0 && <Property value={song.tags.map(tag => tag.name).join(', ')}><Trans>Tags</Trans></Property>}
        </div>
        <CommentList model="song" id={song.id} />
        <CommentForm model="song" id={song.id} isLoggedIn={!!current.id} />
      </div>
    </div>
  );
}

function Property({ children, value }) {
  const text = value.split(',').join(', ');

  if (value === 'null') {
    return null;
  }

  return (
    <p className="text-secondary mb-1 md:pr-4 md:flex-1">
      {children} <span className="text-default md:block md:text-lg">{text}</span>
    </p>
  );
}